/**
 * Custom Web Component for seamless horizontal scrolling (marquee effect).
 * Supports vertical layout via `data-vertical` attribute.
 * Supports scroll direction via `data-direction` attribute (left/right or up/down).
 * Uses IntersectionObserver to optimize performance — only initializes on first viewport entry.
 * Responds to window resize (design mode only), auto-adjusting cloned content.
 * Uses DocumentFragment for batch DOM cloning to minimize layout thrashing.
 */

if (!customElements.get("scroll-seamless")) {
  customElements.define(
    "scroll-seamless",
    class ScrollSeamless extends HTMLElement {
      connectedCallback() {
        this.listContainer = this.querySelector(".scroll-list");
        this.ifVertical = this.hasAttribute("data-vertical");
        this.scrollDirection = this.getAttribute("data-direction") || "left";

        this.speed = parseInt(this.getAttribute("data-speed"), 10) || 40;
        this.currentTrans = 0; // Design mode: offset distance for revealing hidden blocks
        this.lastWindowWidth = window.innerWidth;

        this.observeInView();
        if (window.Shopify.designMode || window.debug) {
          this.debounceWindowSizeChangeHandler = webvista.debounce(
            this.onWindowSizeChange.bind(this),
            500,
          );
          window.addEventListener(
            "resize",
            this.debounceWindowSizeChangeHandler,
          );
        }
      }

      disconnectedCallback() {
        if (this.debounceWindowSizeChangeHandler) {
          window.removeEventListener(
            "resize",
            this.debounceWindowSizeChangeHandler,
          );
        }
      }

      /**
       * Initialize scroll animation
       */
      init() {
        this.getAllItems();
        if (this.items.length < 1) return;

        this.getOriginalTotalSize();
        if (this.originalSize <= 0) return; // Guard against zero-size content
        this.cloneItems();
        this.getAllItems();
        this.getGap();

        const transformDistance = this.originalSize + this.gap;

        if (this.ifVertical) {
          if (this.scrollDirection === "up") {
            this.style.setProperty("--from", 0);
            this.style.setProperty("--end", `-${transformDistance}px`);
          } else {
            this.style.setProperty(
              "--from",
              `${this.clientHeight - this.scrollHeight}px`,
            );
            this.style.setProperty(
              "--end",
              `${this.clientHeight - this.scrollHeight + transformDistance}px`,
            );
          }
        } else {
          if (this.scrollDirection === "left") {
            this.style.setProperty("--from", 0);
            this.style.setProperty("--end", `-${transformDistance}px`);
          } else {
            this.style.setProperty(
              "--from",
              `${this.clientWidth - this.scrollWidth}px`,
            );
            this.style.setProperty(
              "--end",
              `${this.clientWidth - this.scrollWidth + transformDistance}px`,
            );
          }
        }

        this.style.setProperty(
          "--scroll-speed",
          `${transformDistance / this.speed}s`,
        );

        this.setAttribute("data-init", "true");
      }

      /**
       * Handle window resize (design mode only)
       */
      onWindowSizeChange() {
        const currentWidth = window.innerWidth;
        if (currentWidth === this.lastWindowWidth) return;
        this.lastWindowWidth = currentWidth;

        if (!this.hasAttribute("data-init")) return;
        this.removeAttribute("data-init");

        // Remove cloned items and re-initialize
        requestAnimationFrame(() => {
          this.classList.add("hidden"); // Hide first to reduce reflow during DOM removal
          this.querySelectorAll(".item-clone").forEach((item) => item.remove());
          this.classList.remove("hidden");

          this.init();
        });
      }

      /**
       * Observe viewport entry to trigger lazy initialization
       */
      observeInView() {
        const observer = new IntersectionObserver(
          (entries, observer) => {
            if (entries[0].isIntersecting) {
              this.init();
              observer.disconnect();
            }
          },
          {
            root: null,
            rootMargin: "100px 0px 100px 0px",
          },
        );

        observer.observe(this);
      }

      /**
       * Clone items to fill the visible area for seamless looping.
       * Uses DocumentFragment for batched DOM insertion.
       */
      cloneItems() {
        const clientSize = this.ifVertical
          ? this.clientHeight
          : this.clientWidth;

        const times = Math.ceil(clientSize / this.originalSize);
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < times; i++) {
          this.items.forEach((item) => {
            const cloneItem = item.cloneNode(true);
            cloneItem.removeAttribute("data-shopify-editor-block"); // Strip editor attributes from clones
            cloneItem.classList.add("item-clone");
            cloneItem.setAttribute("aria-hidden", true); // Hide clones from assistive devices

            fragment.appendChild(cloneItem);
          });
        }

        this.listContainer.appendChild(fragment);

        webvista.initLazyImages(); // Re-observe cloned lazy images
      }

      /**
       * Collect all scroll items (originals + clones after init)
       */
      getAllItems() {
        this.items = Array.from(this.querySelectorAll(".scroll-item"));
      }

      /**
       * Calculate total size of original items (before cloning)
       */
      getOriginalTotalSize() {
        if (this.ifVertical) {
          this.originalSize =
            this.items[this.items.length - 1].offsetTop -
            this.items[0].offsetTop +
            this.items[this.items.length - 1].offsetHeight;
        } else {
          const firstItem = this.items[0];
          const lastItem = this.items[this.items.length - 1];

          if (webvista.isRTL()) {
            // RTL layout: right-to-left, reversed calculation
            this.originalSize =
              firstItem.offsetLeft - lastItem.offsetLeft + lastItem.offsetWidth;
          } else {
            // LTR layout: left-to-right, normal calculation
            this.originalSize =
              lastItem.offsetLeft - firstItem.offsetLeft + lastItem.offsetWidth;
          }
        }
      }

      /**
       * Calculate gap between items (must be called after cloning, when ≥2 items exist)
       */
      getGap() {
        if (this.items.length < 2) {
          this.gap = 32;
        } else {
          if (this.ifVertical) {
            this.gap =
              this.items[1].offsetTop -
              this.items[0].offsetTop -
              this.items[0].offsetHeight;
          } else {
            const [firstItem, secondItem] = this.items;

            if (webvista.isRTL()) {
              this.gap =
                firstItem.offsetLeft -
                secondItem.offsetLeft -
                firstItem.offsetWidth;
            } else {
              this.gap =
                secondItem.offsetLeft -
                firstItem.offsetLeft -
                firstItem.offsetWidth;
            }
          }
        }
      }

      /**
       * Scroll a specific block into view (design mode only).
       * Translates the list container so the selected block is visible.
       * @param {HTMLElement} item - The block element to reveal
       */
      moveItemVisible(item) {
        if (this.items.indexOf(item) === -1) return;

        const containerRect = this.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();

        let offset;

        if (this.ifVertical) {
          if (itemRect.bottom > containerRect.bottom) {
            offset = containerRect.bottom - itemRect.bottom;
          } else if (itemRect.top < containerRect.top) {
            offset = containerRect.top - itemRect.top;
          } else {
            return;
          }
        } else {
          if (itemRect.right > containerRect.right) {
            offset = containerRect.right - itemRect.right;
          } else if (itemRect.left < containerRect.left) {
            offset = containerRect.left - itemRect.left;
          } else {
            return;
          }
        }

        this.currentTrans = this.currentTrans + offset;

        // Translate the list container to reveal the selected block
        this.listContainer.style.transform = this.ifVertical
          ? `translateY(${this.currentTrans}px)`
          : `translateX(${this.currentTrans}px)`;
      }
    },
  );
}
